import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { DateTime } from 'luxon';
import Icon from '@mdi/react';
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js';
import useEscapeKey from '../hooks/useEscapeKey';
import './SingleMonthDatePicker.css';

/**
 * SingleMonthDatePicker - Lightweight portal-based date picker dropdown
 *
 * Features:
 * - Single month view with prev/next navigation
 * - Portal rendering to avoid modal clipping
 * - Click-outside-to-close
 * - Escape key support
 * - Compact "Dates Unavailable" visual style
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls dropdown visibility
 * @param {Object} props.position - { top: number, left: number } for absolute positioning
 * @param {Object} props.selectedRange - { start: DateTime, end: DateTime } | null
 * @param {Function} props.onSelectRange - Callback when date is selected
 * @param {Function} props.onClose - Callback to close dropdown
 */
const SingleMonthDatePicker = ({
  isOpen,
  position,
  selectedRange = null,
  onSelectRange,
  onClose,
  disablePast = true,
  initialMonth = null
}) => {
  const [currentMonth, setCurrentMonth] = useState(
    (initialMonth || (selectedRange?.start) || DateTime.now()).startOf('month')
  );
  const [rangeStart, setRangeStart] = useState(null);

  // When picker opens, jump to the month of the pre-selected date (or initialMonth)
  // and clear any in-progress range. The component stays MOUNTED when a consumer
  // closes it (isOpen=false), so without the reset a rangeStart from the previous
  // open session survives — and the next open's FIRST click then lands in the
  // "complete range" branch, firing min(staleStart, clicked) instead of the
  // clicked date. Single-date consumers (DateInputWithPicker closes after every
  // selection) saw every other click select the wrong date because of this.
  useEffect(() => {
    if (isOpen) {
      setRangeStart(null);
      const anchor = initialMonth || selectedRange?.start;
      if (anchor) {
        setCurrentMonth(anchor.startOf('month'));
      }
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape-to-close via the shared stack so that when this picker is open inside
  // a modal, Escape closes ONLY the picker (top of the stack) — not the picker
  // and its parent BaseModal at once.
  useEscapeKey(isOpen, onClose);

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      // Close if click is outside the date picker
      if (!e.target.closest('.single-month-date-picker')) {
        onClose();
      }
    };

    // Use mousedown for better UX (fires before click)
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  /**
   * Generate calendar grid for the current month
   * Copied from MonthCalendarView.js (lines 59-105) - simplified version
   */
  const generateMonthGrid = (monthStart) => {
    const weeks = [];
    const today = DateTime.now().startOf('day'); // Today at midnight for comparison
    let currentDate = monthStart.startOf('week'); // Start on Monday

    // Generate 6 weeks to cover all possible month layouts
    for (let week = 0; week < 6; week++) {
      const weekDays = [];

      for (let day = 0; day < 7; day++) {
        const dateStr = currentDate.toISODate();
        const isCurrentMonth = currentDate.month === monthStart.month;
        const isToday = currentDate.hasSame(DateTime.now(), 'day');
        const isPast = disablePast && currentDate < today; // Date is before today
        const isWeekend = currentDate.weekday === 6 || currentDate.weekday === 7; // Saturday or Sunday

        // Check if date is in selected range
        const isInRange = selectedRange &&
          currentDate >= selectedRange.start &&
          currentDate <= selectedRange.end;

        const isRangeStart = selectedRange && currentDate.hasSame(selectedRange.start, 'day');
        const isRangeEnd = selectedRange && currentDate.hasSame(selectedRange.end, 'day');

        weekDays.push({
          date: currentDate,
          dateStr,
          dayNumber: currentDate.day,
          isCurrentMonth,
          isToday,
          isPast,
          isWeekend,
          isInRange,
          isRangeStart,
          isRangeEnd
        });

        currentDate = currentDate.plus({ days: 1 });
      }

      weeks.push(weekDays);
    }

    // Keep all 6 weeks to ensure full month display
    // Ghosted dates (other-month) provide visual context and prevent truncation
    return weeks;
  };

  const grid = useMemo(() => generateMonthGrid(currentMonth), [currentMonth, selectedRange]);

  /**
   * Handle date click for range selection
   * Copied from MonthCalendarView.js (lines 110-128) - simplified
   */
  const handleDateClick = (dayObj) => {
    // Prevent selection of ghosted dates (other-month dates) and past dates
    if (!dayObj.isCurrentMonth || dayObj.isPast) return;

    if (!rangeStart) {
      // Start new range - immediately set as single-day range
      setRangeStart(dayObj.date);
      onSelectRange({ start: dayObj.date, end: dayObj.date });
    } else {
      // Complete range
      const start = rangeStart < dayObj.date ? rangeStart : dayObj.date;
      const end = rangeStart < dayObj.date ? dayObj.date : rangeStart;

      onSelectRange({ start, end });
      setRangeStart(null);
    }
  };

  /**
   * Get CSS classes for a day cell
   * Copied from MonthCalendarView.js (lines 133-146) - simplified
   */
  const getDayClasses = (dayObj) => {
    const classes = ['calendar-day'];

    if (!dayObj.isCurrentMonth) classes.push('other-month');
    if (dayObj.isPast) classes.push('past-date');
    if (dayObj.isToday) classes.push('today');
    if (dayObj.isWeekend) classes.push('weekend');
    if (dayObj.isInRange) classes.push('in-range');
    if (dayObj.isRangeStart) classes.push('range-start');
    if (dayObj.isRangeEnd) classes.push('range-end');
    if (rangeStart && dayObj.date.hasSame(rangeStart, 'day')) classes.push('range-start-temp');

    return classes.join(' ');
  };

  // Check if we can navigate to previous month
  const canNavigatePrev = !disablePast || currentMonth.startOf('month') > DateTime.now().startOf('month');

  // Month navigation handlers
  const handlePrevMonth = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (canNavigatePrev) {
      setCurrentMonth(prev => prev.minus({ months: 1 }));
    }
  };

  const handleNextMonth = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentMonth(prev => prev.plus({ months: 1 }));
  };

  if (!isOpen) return null;

  // Portal render to document.body
  return ReactDOM.createPortal(
    <div
      className="single-month-date-picker"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`
      }}
    >
      {/* Month Header with Navigation */}
      <div className="calendar-header">
        <button
          type="button"
          className="nav-button"
          onClick={handlePrevMonth}
          disabled={!canNavigatePrev}
          aria-label="Previous month"
        >
          <Icon path={mdiChevronLeft} size={0.8} />
        </button>

        <h3 className="month-title">
          {currentMonth.toFormat('MMMM yyyy')}
        </h3>

        <button
          type="button"
          className="nav-button"
          onClick={handleNextMonth}
          aria-label="Next month"
        >
          <Icon path={mdiChevronRight} size={0.8} />
        </button>
      </div>

      {/* Weekday Headers */}
      <div className="weekdays">
        {['M', 'T', 'W', 'Th', 'F', 'S', 'S'].map((day, index) => (
          <div
            key={`${day}-${index}`}
            className={`weekday ${(index === 5 || index === 6) ? 'weekend-header' : ''}`}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="calendar-grid">
        {grid.map((week, weekIndex) => (
          <React.Fragment key={weekIndex}>
            {week.map((dayObj, dayIndex) => (
              <div
                key={dayIndex}
                className={getDayClasses(dayObj)}
                onClick={() => handleDateClick(dayObj)}
              >
                {dayObj.dayNumber}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>,
    document.body
  );
};

export default SingleMonthDatePicker;
